import { MESSENGER_QUICK_REPLIES_MAX, WORKFLOW_CHANNELS, WORKFLOW_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon } from "@sagot/ui";

const N = "engage.workflows.builder.channelNotes";

/** How customer-facing steps look on Messenger and email (scope 20.1); an empty channel list means every channel. */
export function ChannelNotes({ channels }: { channels: readonly string[] }) {
  const { t } = useTranslation();
  const effective = channels.length > 0 ? channels : WORKFLOW_CHANNELS;
  const notes = [
    effective.includes("messenger") ? { channel: "messenger", text: t(`${N}.messenger`, { replies: MESSENGER_QUICK_REPLIES_MAX, chars: WORKFLOW_LIMITS.messengerLabel }) } : null,
    effective.includes("email") ? { channel: "email", text: t(`${N}.email`) } : null,
    effective.includes("api") ? { channel: "api", text: t(`${N}.api`) } : null,
  ].filter((note) => note !== null);
  if (notes.length === 0) return null;
  return (
    <ul className="space-y-1.5 rounded-md bg-info-soft p-3 text-xs text-fg-muted" aria-label={t(`${N}.title`)} data-testid="channel-notes">
      {notes.map((note) => (
        <li key={note.channel} className="flex items-start gap-2">
          <Icon name="info" className="mt-0.5 size-3.5 shrink-0 text-info" />
          <span>{note.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** Which channels a conversation-control step says something about: `web` is the widget, the web app and mobile; `other` the ones that cannot hide a composer. */
const STEP_NOTES: Readonly<Record<string, readonly string[]>> = {
  collect_reply: ["web", "messenger", "email", "api"],
  verify_email: ["web", "messenger", "email", "api", "preview"],
  disable_reply: ["web", "other"],
  enable_reply: ["web", "other"],
  snooze: ["behind"],
  set_language: ["behind"],
  show_article: ["web", "messenger", "email", "api"],
  add_summary_note: ["behind"],
};

/** How one conversation-control step behaves on each channel (plan section 4a-4d); nothing for the other steps. */
export function StepChannelNotes({ type }: { type: string }) {
  const { t } = useTranslation();
  const channels = STEP_NOTES[type];
  if (!channels) return null;
  return (
    <ul className="space-y-1.5 rounded-md bg-info-soft p-3 text-xs text-fg-muted" aria-label={t(`${N}.title`)} data-testid="step-channel-notes">
      {channels.map((channel) => (
        <li key={channel} className="flex items-start gap-2">
          <Icon name="info" className="mt-0.5 size-3.5 shrink-0 text-info" />
          <span>{t(`${N}.steps.${type}.${channel}`)}</span>
        </li>
      ))}
    </ul>
  );
}
